'use client'

import React, { useState, useEffect } from 'react'
import Image from 'next/image'
import { createPortal } from 'react-dom'
import { X, Check, Copy, Clock, ThumbsUp, ThumbsDown, ChevronDown, ShieldCheck, Calendar, ExternalLink } from 'lucide-react'
import { toast } from 'react-toastify'
import { discardHTMLTags, getCurrencySymbol, getBaseImageUrl, calculateOfferDuration, getFinalDiscountTag } from '@/constants/hooks'
import { Offer, ProductData } from '@/services/dataTypes'
import { copyToClipboard } from '../utils/clipboard'
import DiscountTag from './DiscountTag'

const C = {
    PRIMARY: '#F8F246',
    SECONDARY: '#2CBBC3',
    BROWN: '#8C5826',
    BORDER: '#f0ede8',
}

interface Props {
    product: Offer | ProductData
    merchantName: string
    merchantLogo: string
    domain: string
    isOpen?: boolean
    onClose?: () => void
}

const ProductDetailsModalContent = ({ product, merchantName, merchantLogo, domain, onClose }: Props & { onClose: () => void }) => {
    const [copied, setCopied] = useState(false)
    const [dealLoading, setDealLoading] = useState(false)
    const [hasLiked, setHasLiked] = useState(false)
    const [isExpanded, setIsExpanded] = useState(false)

    useEffect(() => {
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = 'unset'
        }
    }, [])

    useEffect(() => {
        setCopied(false)
        setDealLoading(false)
        setHasLiked(false)
        setIsExpanded(false)
    }, [product?.unique_id])

    const handleCopyCode = async () => {
        if (!product?.coupon_code) return
        await copyToClipboard(product.coupon_code.trim())
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    const handleLikeStatus = () => {
        setHasLiked(true)
        toast.success('Thank you for your feedback!', { autoClose: 2000 })
    }

    const originalPrice = product?.original_price ? parseFloat(product.original_price) : 0
    const salePrice = product?.sale_price ? parseFloat(product.sale_price) : 0
    const discountPct = originalPrice > 0 && salePrice > 0 ? Math.round(((originalPrice - salePrice) / originalPrice) * 100) : null
    const tag = getFinalDiscountTag(product?.offer_title || product?.offer_detail, discountPct)
    const currency = getCurrencySymbol(product?.currency)
    const duration = calculateOfferDuration(product?.end_date ?? null)
    const isCoupon = !!product?.coupon_code
    const isProduct = product?.offer_type?.name === 'product'

    const imageSrc = (isProduct && product?.product_image)
        ? getBaseImageUrl(domain, product.product_image, '')
        : getBaseImageUrl(domain, merchantLogo, '')

    const finalUrl = product?.url?.startsWith('/') ? product.url.replace(/^\/+/, '') : (product?.url ?? '')
    const absoluteOutUrl = `/${finalUrl}`
    const isLifetime = !product?.end_date || product?.end_date?.toLowerCase().includes('lifetime') || product?.end_date?.toLowerCase().includes('life time') || product?.end_date?.toLowerCase().includes('no expiry')

    const cleanDetail = discardHTMLTags(product?.offer_detail || '')
    const isLongDetail = cleanDetail.length > 200

    return (
        <div
            className="fixed inset-0 z-[99999] flex items-center justify-center p-3 sm:p-5 overflow-y-auto"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
        >
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/60 backdrop-blur-xs transition-opacity duration-300" />

            {/* Modal Window */}
            <div
                className="relative bg-white rounded-3xl sm:rounded-[2.5rem] shadow-2xl max-w-2xl w-full max-h-[92vh] sm:max-h-[85vh] overflow-hidden flex flex-col border border-[#f0ede8] z-10 animate-in zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close Trigger Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 sm:top-6 sm:right-6 z-30 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-white text-gray-500 hover:text-gray-900 border border-[#f0ede8] transition-all duration-300 shadow-md hover:scale-105 active:scale-95 cursor-pointer"
                    aria-label="Close"
                >
                    <X size={16} />
                </button>

                {/* Header Area */}
                <div className="flex-shrink-0 px-5 sm:px-8 pt-6 sm:pt-8 pb-4 relative border-b border-[#f0ede8]">
                    <div className="flex items-start gap-4 sm:gap-5">
                        {/* Brand Logo Container */}
                        {imageSrc && (
                            <div className="relative shrink-0 w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-2xl sm:rounded-3xl p-2 shadow-md border border-[#f0ede8] flex items-center justify-center">
                                <Image
                                    src={imageSrc}
                                    alt={merchantName || 'Store'}
                                    fill
                                    className="object-contain p-2"
                                />
                            </div>
                        )}

                        {/* Title & Badges */}
                        <div className="flex-1 min-w-0 pr-6 sm:pr-8">
                            {/* Badges row */}
                            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-2">
                                {tag && <DiscountTag tag={tag} />}
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-[9px] font-black uppercase tracking-widest text-emerald-700 bg-emerald-50 rounded-md border border-emerald-100">
                                    <ShieldCheck size={11} className="text-emerald-600" />
                                    Verified
                                </span>
                                {product?.end_date && !isLifetime && (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-[9px] font-black uppercase tracking-widest text-amber-800 bg-amber-50 rounded-md border border-amber-200">
                                        <Calendar size={11} className="text-amber-600" />
                                        {calculateOfferDuration(product?.end_date)}
                                    </span>
                                )}
                            </div>

                            <h2 className="text-base sm:text-lg font-black text-gray-900 leading-snug line-clamp-2">
                                {discardHTMLTags(product?.offer_title || '')}
                            </h2>
                            <p className="text-xs font-semibold text-gray-500 mt-0.5">
                                Offered by <span className="text-gray-800 font-bold">{merchantName}</span>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Scrollable Content Body */}
                <div className="flex-1 overflow-y-auto px-5 sm:px-8 py-5 sm:py-6 space-y-5">
                    
                    {/* Pricing Box */}
                    {(salePrice > 0 || originalPrice > 0) && (
                        <div className="inline-flex items-center gap-2.5 px-4 py-2 border border-[#f0ede8] rounded-2xl shadow-2xs">
                            <div className="flex items-baseline gap-2.5">
                                {salePrice > 0 && (
                                    <span className="text-2xl font-black text-gray-900 leading-none">
                                        {currency}{salePrice.toFixed(2)}
                                    </span>
                                )}
                                {originalPrice > 0 && (
                                    <span className="text-sm sm:text-base line-through text-gray-400 font-medium leading-none">
                                        {currency}{originalPrice.toFixed(2)}
                                    </span>
                                )}
                                {discountPct && discountPct > 0 && (
                                    <span className="text-xs font-bold text-emerald-700 ml-1">
                                        (-{discountPct}%)
                                    </span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Coupon Code Section (if present) */}
                    {isCoupon && (
                        <div className="border-2 border-dashed border-[#8C5826]/30 p-4 sm:p-5 rounded-2xl sm:rounded-3xl flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
                            <div className="text-center sm:text-left">
                                <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                    Coupon Code
                                </span>
                                <p className="text-xl sm:text-2xl font-mono font-black text-[#8C5826] tracking-wider mt-0.5 select-all">
                                    {product.coupon_code}
                                </p>
                            </div>
                            <button
                                onClick={handleCopyCode}
                                className="w-full sm:w-auto px-6 py-3 text-white text-xs font-black uppercase tracking-widest rounded-2xl transition-all duration-300 shadow-md hover:opacity-90 active:scale-95 flex items-center justify-center gap-2 cursor-pointer"
                                style={{ background: copied ? '#10B981' : C.BROWN }}
                            >
                                {copied ? <Check size={14} /> : <Copy size={14} />}
                                <span>{copied ? 'Copied!' : 'Copy Code'}</span>
                            </button>
                        </div>
                    )}

                    {/* Description Section */}
                    {cleanDetail && (
                        <div className="space-y-2">
                            <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none">
                                Description
                            </h3>
                            <div className="p-4 sm:p-5 rounded-2xl border border-[#f0ede8]">
                                <p className={`text-gray-700 text-xs sm:text-sm leading-relaxed ${isLongDetail && !isExpanded ? 'line-clamp-3' : ''}`}>
                                    {cleanDetail}
                                </p>
                                {isLongDetail && (
                                    <button
                                        type="button"
                                        onClick={() => setIsExpanded(!isExpanded)}
                                        className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-black text-[#8C5826] hover:text-[#5e3814] transition-colors cursor-pointer group"
                                    >
                                        <span>{isExpanded ? 'See Less' : 'See More'}</span>
                                        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Direct Shop Action Trigger */}
                    <button
                        onClick={() => {
                            setDealLoading(true)
                            setTimeout(() => {
                                window.open(absoluteOutUrl, '_blank', 'noopener,noreferrer')
                                setDealLoading(false)
                            }, 600)
                        }}
                        disabled={dealLoading}
                        className="w-full h-12 sm:h-14 flex items-center justify-center gap-3 font-black rounded-2xl transition-all duration-300 shadow-md hover:scale-[1.01] active:scale-95 text-xs uppercase tracking-widest cursor-pointer disabled:opacity-60"
                        style={{
                            background: `linear-gradient(135deg, ${C.PRIMARY} 0%, #ffd452 100%)`,
                            color: C.BROWN,
                            border: '1px solid rgba(140, 88, 38, 0.15)',
                            boxShadow: '0 2px 10px rgba(248, 242, 70, 0.4)',
                        }}
                    >
                        {dealLoading ? (
                            <span>Redirecting...</span>
                        ) : (
                            <>
                                <span>{isCoupon ? 'Shop Now with Code' : isProduct ? 'Buy Now on Store' : 'Go to Merchant Website'}</span>
                                <ExternalLink size={15} />
                            </>
                        )}
                    </button>
                </div>

                {/* Footer Area ("Was this offer helpful?") */}
                <div className="p-4 sm:px-8 sm:py-5 border-t border-[#f0ede8] flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 flex-shrink-0 bg-white">
                    <span className="text-[11px] sm:text-xs font-black text-gray-400 uppercase tracking-widest">
                        Was this offer helpful?
                    </span>
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <button
                            onClick={handleLikeStatus}
                            disabled={hasLiked}
                            className={`flex-1 sm:flex-none justify-center inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs font-extrabold transition-all duration-200 border cursor-pointer ${
                                hasLiked
                                    ? 'bg-emerald-600 text-white border-emerald-600 shadow-md scale-105'
                                    : 'bg-emerald-500 hover:bg-emerald-600 text-white border-emerald-500 shadow-sm hover:scale-[1.02] active:scale-95'
                            }`}
                        >
                            <ThumbsUp size={13} />
                            <span>Yes</span>
                        </button>
                        <button
                            onClick={handleLikeStatus}
                            disabled={hasLiked}
                            className={`flex-1 sm:flex-none justify-center inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs font-extrabold transition-all duration-200 border cursor-pointer ${
                                hasLiked
                                    ? 'bg-rose-600 text-white border-rose-600 shadow-md scale-105'
                                    : 'bg-rose-500 hover:bg-rose-600 text-white border-rose-500 shadow-sm hover:scale-[1.02] active:scale-95'
                            }`}
                        >
                            <ThumbsDown size={13} />
                            <span>No</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

const ProductDetailsModal = ({ product, merchantName, merchantLogo, domain, isOpen, onClose }: Props) => {
    const [mounted, setMounted] = useState(false)
    const [internalOpen, setInternalOpen] = useState(false)
    useEffect(() => { setMounted(true) }, [])

    // Controlled mode — with isOpen & onClose
    if (onClose) {
        if (!mounted || isOpen === false) return null
        return createPortal(
            <ProductDetailsModalContent
                product={product}
                merchantName={merchantName}
                merchantLogo={merchantLogo}
                domain={domain}
                onClose={onClose}
            />,
            document.body
        )
    }

    // Self-contained trigger button mode
    return (
        <>
            <button
                onClick={e => { e.preventDefault(); e.stopPropagation(); setInternalOpen(true) }}
                className="inline-flex items-center gap-1 text-[11px] font-bold transition-colors hover:opacity-70 cursor-pointer text-[#8C5826]"
            >
                <span>View Details</span>
            </button>
            {mounted && internalOpen && createPortal(
                <ProductDetailsModalContent
                    product={product}
                    merchantName={merchantName}
                    merchantLogo={merchantLogo}
                    domain={domain}
                    onClose={() => setInternalOpen(false)}
                />,
                document.body
            )}
        </>
    )
}

export default ProductDetailsModal
